import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, AuthState, LlmPreset } from '@/types';
import api from '@/services/api';

interface AuthActions {
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  login: (user: User, token: string) => void;
  logout: () => void;
  setLoading: (isLoading: boolean) => void;
  updateLlmPreset: (preset: LlmPreset) => Promise<void>;
}

type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      ...initialState,

      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
        }),

      setToken: (token) =>
        set({
          token,
        }),

      login: (user, token) =>
        set({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
        }),

      logout: () =>
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        }),

      setLoading: (isLoading) =>
        set({
          isLoading,
        }),

      updateLlmPreset: async (preset: LlmPreset) => {
        try {
          interface ApiUserResponse {
            success: boolean;
            data: {
              attributes: {
                preferred_llm_preset?: LlmPreset;
                effective_llm_preset?: LlmPreset;
              };
            };
          }
          const response = await api.patch<ApiUserResponse>('/me', {
            user: { preferred_llm_preset: preset },
          });

          if (response.data.success) {
            const attrs = response.data.data.attributes;
            set((state) => ({
              user: state.user
                ? {
                    ...state.user,
                    preferredLlmPreset: attrs.preferred_llm_preset,
                    effectiveLlmPreset: attrs.effective_llm_preset,
                  }
                : null,
            }));
          }
        } catch (error) {
          console.error('Failed to update LLM preset:', error);
          throw error;
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
